import { Alert, Button, Group } from '@mantine/core';
import { useForm } from '@mantine/form';
import { useEffect, useId, useRef, useState, type RefObject } from 'react';
import { InputPassword, Stack, Typography } from '@xhr-labs/core-ui';
import { getErrorMessage } from '@/lib/notifications';
import { settingsApi, type ChangePasswordRequest } from './settings-api';

interface PasswordChangeFormProps {
  currentPasswordInputRef?: RefObject<HTMLInputElement | null>;
  onSuccess?: () => void;
}

export function PasswordChangeForm({
  currentPasswordInputRef,
  onSuccess,
}: PasswordChangeFormProps) {
  const formId = useId();
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const savedTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  useEffect(() => {
    return () => {
      if (savedTimerRef.current) clearTimeout(savedTimerRef.current);
    };
  }, []);

  const form = useForm<ChangePasswordRequest>({
    initialValues: {
      currentPassword: '',
      newPassword: '',
      confirmPassword: '',
    },
    validate: {
      currentPassword: (value) => (!value.trim() ? 'Current password is required' : null),
      newPassword: (value) => (value.length < 8 ? 'Password must be at least 8 characters' : null),
      confirmPassword: (value, values) =>
        value !== values.newPassword ? 'Passwords do not match' : null,
    },
  });
  const currentPasswordError = form.errors['currentPassword'];
  const newPasswordError = form.errors['newPassword'];
  const confirmPasswordError = form.errors['confirmPassword'];
  const currentPasswordErrorMessage =
    typeof currentPasswordError === 'string' ? currentPasswordError : null;
  const newPasswordErrorMessage = typeof newPasswordError === 'string' ? newPasswordError : null;
  const confirmPasswordErrorMessage =
    typeof confirmPasswordError === 'string' ? confirmPasswordError : null;

  const handleSubmit = async (values: ChangePasswordRequest) => {
    setError(null);
    setSaved(false);
    setIsChangingPassword(true);
    if (savedTimerRef.current) clearTimeout(savedTimerRef.current);
    try {
      await settingsApi.changePassword(values);
      form.reset();
      if (onSuccess) {
        onSuccess();
        return;
      }
      setSaved(true);
      savedTimerRef.current = setTimeout(() => {
        setSaved(false);
      }, 3000);
    } catch (changeError) {
      setError(getErrorMessage(changeError));
    } finally {
      setIsChangingPassword(false);
    }
  };

  return (
    <form onSubmit={form.onSubmit(handleSubmit)}>
      <Stack gap="md">
        {error && (
          <Alert color="red" variant="light">
            {error}
          </Alert>
        )}

        {saved && (
          <Alert color="green" variant="light">
            Password changed successfully
          </Alert>
        )}

        <Stack gap="xs">
          <label htmlFor={`${formId}-current-password`}>
            <Typography variant="body-small">Current Password</Typography>
          </label>
          <InputPassword
            {...(currentPasswordInputRef ? { ref: currentPasswordInputRef } : {})}
            id={`${formId}-current-password`}
            placeholder="Enter current password"
            autoComplete="current-password"
            {...form.getInputProps('currentPassword')}
            error={currentPasswordErrorMessage !== null}
            aria-invalid={currentPasswordErrorMessage !== null}
            aria-describedby={
              currentPasswordErrorMessage !== null ? `${formId}-current-password-error` : undefined
            }
          />
          {currentPasswordErrorMessage !== null && (
            <Typography
              id={`${formId}-current-password-error`}
              variant="body-small"
              color="color-content-negative-strong"
            >
              {currentPasswordErrorMessage}
            </Typography>
          )}
        </Stack>

        <Stack gap="xs">
          <label htmlFor={`${formId}-new-password`}>
            <Typography variant="body-small">New Password</Typography>
          </label>
          {newPasswordErrorMessage === null && (
            <Typography
              id={`${formId}-new-password-hint`}
              variant="body-small"
              color="color-content-neutral-medium"
            >
              Must be at least 8 characters
            </Typography>
          )}
          <InputPassword
            id={`${formId}-new-password`}
            placeholder="Enter new password"
            autoComplete="new-password"
            {...form.getInputProps('newPassword')}
            error={newPasswordErrorMessage !== null}
            aria-invalid={newPasswordErrorMessage !== null}
            aria-describedby={
              newPasswordErrorMessage !== null
                ? `${formId}-new-password-error`
                : `${formId}-new-password-hint`
            }
          />
          {newPasswordErrorMessage !== null && (
            <Typography
              id={`${formId}-new-password-error`}
              variant="body-small"
              color="color-content-negative-strong"
            >
              {newPasswordErrorMessage}
            </Typography>
          )}
        </Stack>

        <Stack gap="xs">
          <label htmlFor={`${formId}-confirm-password`}>
            <Typography variant="body-small">Confirm New Password</Typography>
          </label>
          <InputPassword
            id={`${formId}-confirm-password`}
            placeholder="Confirm new password"
            autoComplete="new-password"
            {...form.getInputProps('confirmPassword')}
            error={confirmPasswordErrorMessage !== null}
            aria-invalid={confirmPasswordErrorMessage !== null}
            aria-describedby={
              confirmPasswordErrorMessage !== null ? `${formId}-confirm-password-error` : undefined
            }
          />
          {confirmPasswordErrorMessage !== null && (
            <Typography
              id={`${formId}-confirm-password-error`}
              variant="body-small"
              color="color-content-negative-strong"
            >
              {confirmPasswordErrorMessage}
            </Typography>
          )}
        </Stack>

        <Group justify="flex-end" mt="md">
          <Button type="submit" loading={isChangingPassword}>
            Update Password
          </Button>
        </Group>
      </Stack>
    </form>
  );
}
